import { useTranslation } from 'next-i18next';
import styles from './style.module.scss';
import Button, { PRIMARY_THEME } from 'components/control/button';
import Image from 'next/image';
import { useState, useRef } from 'react';
import { useIsSmallDesktop } from 'hooks/use-size-class';
import { ReadonlyDeep } from 'type-fest';
import HtmlTrans from 'components/html-trans';

export type TiltedCardsProps = ReadonlyDeep<{
  heading: {
    title: string;
    buttonText: string;
    buttonLink: string;
  };
  backgroundColor: string;
  cards: Array<{
    title: string;
    image: string;
    imgAlt: string;
    description: string;
    backgroundColor: string;
    isDark?: boolean;
    link?: string;
  }>;
}>;

export default function TiltedCards({ heading, backgroundColor, cards }: TiltedCardsProps) {
  const { t, ready: i18nReady } = useTranslation();
  const [activeCard, setActiveCard] = useState(0);
  const lastActivatedTimeRef = useRef<number>(0);
  const isSmallDesktop = useIsSmallDesktop();
  const offset = isSmallDesktop ? 22 : 14; // vh
  const cardHeight = isSmallDesktop ? 60 : 70; // vh

  const handleCardEnter = (index: number) => {
    if (index !== activeCard) {
      lastActivatedTimeRef.current = Date.now();
    }
    setActiveCard(index);
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    // Prevent click if card was just activated (within 300ms)
    // this is to fix an issue with mobile where tapping triggers mouseenter and mouseclick
    const timeSinceActivation = Date.now() - lastActivatedTimeRef.current;
    if (timeSinceActivation < 300) {
      e.preventDefault();
      return false;
    }
  };

  return ( !i18nReady ? null :
    <div className={styles.component} style={{ backgroundColor: backgroundColor }}>

      <div className={styles.heading}>
        <h1>{t(heading.title)}</h1>
        <a href={heading.buttonLink} target='_blank' rel='noopener noreferrer'>
          <Button theme={PRIMARY_THEME} label={t(heading.buttonText)} ></Button>
        </a>
      </div>
      <div className={styles.cards} style={{ height: `${((cards.length - 1) * offset) + cardHeight}vh`, overflow: 'hidden' }}>
        {cards.map((card, index) => {
          const baseOffset = index <= activeCard
            ? index * offset
            : (activeCard * offset) + cardHeight + (index - (activeCard + 1)) * offset;
          return (
            <div className={styles['card-container']} key={index} 
              style={{
                transform: `translateY(${baseOffset}vh)`,
                zIndex: index + 50,
                height: `${cardHeight}vh`,
              }}
              onMouseEnter={() => handleCardEnter(index)}
            >
              <div className={styles['card-content']} style={{ backgroundColor: card.backgroundColor, color: card.isDark ? '#FFFFFF' : '#000000' }}>
                <div className={styles.number}>{(index + 1).toString().padStart(2, '0')}</div>
                <div className={styles.text}>
                  <div className={styles['text-content']}>
                    <div className={styles['text-content-title']}>
                      {card.link ? (
                        <a 
                          href={card.link}
                          target='_blank' 
                          rel='noopener noreferrer'
                          data-disabled={index !== activeCard}
                          onClick={handleLinkClick}
                        >
                          <h2>{t(card.title)}</h2>
                        </a>
                      ) : (
                        <h2>{t(card.title)}</h2>
                      )}
                    </div>
                    <p style={{ color: card.isDark ? '#FFFFFF' : '#595959' }}><HtmlTrans t={t} i18nKey={card.description} /></p>
                  </div>
                </div>
                <div className={styles.picture}>
                  <div className={styles['picture-container']}>
                    <Image src={card.image} alt={card.imgAlt} fill loading='lazy' sizes='30vw' style={{ objectFit: 'cover' }} />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}